/**
 * CategoryReportPage.js
 * Ausführliche Auswertung nach Leistungskategorie
 * Tabelle mit aufklappbaren Unterkategorien, Filter, CSV-Export
 */
import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format, startOfYear, startOfMonth, endOfMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FolderTree,
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCcw,
  Clock,
  Euro,
  TrendingUp,
  Filter,
  Calendar,
  Building2,
} from 'lucide-react';

export default function CategoryReportPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  
  // Filter state
  const [dateFrom, setDateFrom] = useState(format(startOfYear(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [customerId, setCustomerId] = useState('');
  const [groupMode, setGroupMode] = useState('main');
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  }, [api]);

  // Fetch report data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        group: groupMode,
      };
      if (customerId) {
        params.customer_id = customerId;
      }
      
      const res = await api.get('/reports/by-category', { params });
      setData(res.data);
      
      // Expand all by default
      const ids = new Set(res.data.categories.map(c => c.category_id));
      setExpandedCategories(ids);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Fehler beim Laden der Auswertung');
    } finally {
      setLoading(false);
    }
  }, [api, dateFrom, dateTo, customerId, groupMode]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!data?.categories) return;
    
    const rows = [
      ['Kategorie', 'Stunden gesamt', 'Abrechenbar (h)', 'Nicht abrechenbar (h)', 'Betrag (€)', 'Anteil (%)']
    ];
    
    data.categories.forEach(cat => {
      rows.push([
        cat.category_name,
        cat.hours.toFixed(2),
        cat.billable_hours.toFixed(2),
        cat.non_billable_hours.toFixed(2),
        cat.billable_amount.toFixed(2),
        cat.hours_percent.toFixed(1)
      ]);
      
      // Add subcategories
      if (cat.subcategories?.length > 0) {
        cat.subcategories.forEach(sub => {
          rows.push([
            `  → ${sub.category_name}`,
            sub.hours.toFixed(2),
            sub.billable_hours.toFixed(2),
            sub.non_billable_hours.toFixed(2),
            sub.billable_amount.toFixed(2),
            sub.hours_percent.toFixed(1)
          ]);
        });
      }
    });
    
    // Add uncategorized
    if (data.uncategorized?.hours > 0) {
      rows.push([
        'Ohne Kategorie',
        data.uncategorized.hours.toFixed(2),
        data.uncategorized.billable_hours.toFixed(2),
        data.uncategorized.non_billable_hours.toFixed(2),
        data.uncategorized.billable_amount.toFixed(2),
        data.uncategorized.hours_percent.toFixed(1)
      ]);
    }
    
    // Add totals
    rows.push([]);
    rows.push([
      'GESAMT',
      data.totals.total_hours.toFixed(2),
      data.totals.billable_hours.toFixed(2),
      data.totals.non_billable_hours.toFixed(2),
      data.totals.billable_amount.toFixed(2),
      '100.0'
    ]);
    
    const csvContent = rows.map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kategorieauswertung_${dateFrom}_${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('CSV exportiert');
  };

  // Quick period presets
  const setPeriodPreset = (preset) => {
    const now = new Date();
    switch (preset) {
      case 'year':
        setDateFrom(format(startOfYear(now), 'yyyy-MM-dd'));
        setDateTo(format(now, 'yyyy-MM-dd'));
        break;
      case 'month':
        setDateFrom(format(startOfMonth(now), 'yyyy-MM-dd'));
        setDateTo(format(endOfMonth(now), 'yyyy-MM-dd'));
        break;
      case 'q1':
        setDateFrom(`${now.getFullYear()}-01-01`);
        setDateTo(`${now.getFullYear()}-03-31`);
        break;
      case 'q2':
        setDateFrom(`${now.getFullYear()}-04-01`);
        setDateTo(`${now.getFullYear()}-06-30`);
        break;
      case 'q3':
        setDateFrom(`${now.getFullYear()}-07-01`);
        setDateTo(`${now.getFullYear()}-09-30`);
        break;
      case 'q4':
        setDateFrom(`${now.getFullYear()}-10-01`);
        setDateTo(`${now.getFullYear()}-12-31`);
        break;
      default:
        break;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  };

  return (
    <div className="p-6 space-y-6" data-testid="category-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FolderTree className="h-6 w-6" />
            Auswertung nach Leistungskategorie
          </h1>
          <p className="text-muted-foreground mt-1">
            Stunden und Umsätze gruppiert nach Leistungsart
          </p>
        </div>
        <Button
          onClick={handleExportCSV}
          disabled={!data?.categories?.length}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          CSV Export
        </Button>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-glass p-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Filter</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date From */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Von</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-card border-border text-foreground"
              data-testid="date-from-input"
            />
          </div>
          
          {/* Date To */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Bis</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-card border-border text-foreground"
              data-testid="date-to-input"
            />
          </div>
          
          {/* Customer */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Kunde</Label>
            <Select value={customerId || 'all'} onValueChange={(val) => setCustomerId(val === 'all' ? '' : val)}>
              <SelectTrigger className="bg-card border-border text-foreground" data-testid="customer-filter-select">
                <SelectValue placeholder="Alle Kunden" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Alle Kunden</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Group Mode */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Gruppierung</Label>
            <Select value={groupMode} onValueChange={setGroupMode}>
              <SelectTrigger className="bg-card border-border text-foreground" data-testid="group-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="main">Nur Hauptkategorien</SelectItem>
                <SelectItem value="sub">Inkl. Unterkategorien</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Quick Presets */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground mr-2">Schnellauswahl:</span>
          <button onClick={() => setPeriodPreset('month')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Lfd. Monat
          </button>
          <button onClick={() => setPeriodPreset('year')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Lfd. Jahr
          </button>
          <button onClick={() => setPeriodPreset('q1')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Q1
          </button>
          <button onClick={() => setPeriodPreset('q2')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Q2
          </button>
          <button onClick={() => setPeriodPreset('q3')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Q3
          </button>
          <button onClick={() => setPeriodPreset('q4')} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded bg-muted hover:bg-muted">
            Q4
          </button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      {!loading && data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-glass p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <span className="text-xs text-muted-foreground">Stunden gesamt</span>
            </div>
            <p className="text-2xl font-mono font-bold text-foreground">{data.totals.total_hours.toFixed(1)}h</p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card-glass p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-lime-600 dark:text-lime-400" />
              <span className="text-xs text-muted-foreground">Abrechenbar</span>
            </div>
            <p className="text-2xl font-mono font-bold text-lime-600 dark:text-lime-400">{data.totals.billable_hours.toFixed(1)}h</p>
            <p className="text-xs text-muted-foreground mt-1">
              {((data.totals.billable_hours / data.totals.total_hours) * 100 || 0).toFixed(0)}% der Stunden
            </p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-glass p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Nicht abrechenbar</span>
            </div>
            <p className="text-2xl font-mono font-bold text-muted-foreground">{data.totals.non_billable_hours.toFixed(1)}h</p>
            <p className="text-xs text-muted-foreground mt-1">
              {((data.totals.non_billable_hours / data.totals.total_hours) * 100 || 0).toFixed(0)}% der Stunden
            </p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card-glass p-4">
            <div className="flex items-center gap-2 mb-2">
              <Euro className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs text-muted-foreground">Umsatz (abrechenbar)</span>
            </div>
            <p className="text-2xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(data.totals.billable_amount)}</p>
          </motion.div>
        </div>
      )}

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-glass overflow-hidden"
      >
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <RefreshCcw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.categories?.length && !data?.uncategorized?.hours ? (
          <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
            <FolderTree className="h-12 w-12 mb-3 opacity-50" />
            <p>Keine Daten für diesen Zeitraum</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground w-[300px]">Kategorie</TableHead>
                <TableHead className="text-muted-foreground text-right">Stunden</TableHead>
                <TableHead className="text-muted-foreground text-right">Abrechenbar</TableHead>
                <TableHead className="text-muted-foreground text-right">Nicht abr.</TableHead>
                <TableHead className="text-muted-foreground text-right">Betrag</TableHead>
                <TableHead className="text-muted-foreground text-right">Anteil</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.categories.map((cat) => (
                <Fragment key={cat.category_id}>
                  <TableRow
                    className="border-border cursor-pointer hover:bg-muted/50"
                    onClick={() => (Array.isArray(cat.subcategories) ? cat.subcategories : []).length > 0 && toggleCategory(cat.category_id)}
                    data-testid={`category-row-${cat.category_id}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {(Array.isArray(cat.subcategories) ? cat.subcategories : []).length > 0 && (
                          expandedCategories.has(cat.category_id) 
                            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        {cat.color && (
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        )}
                        <span className="text-foreground">{cat.category_name}</span>
                        {!cat.billable_default && (
                          <Badge variant="secondary" className="text-[10px] bg-muted text-muted-foreground">
                            Nicht abr.
                          </Badge>
                        )}
                        {(Array.isArray(cat.subcategories) ? cat.subcategories : []).length > 0 && (
                          <span className="text-xs text-muted-foreground">({cat.subcategories.length})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-foreground">{cat.hours.toFixed(1)}h</TableCell>
                    <TableCell className="text-right font-mono text-lime-600 dark:text-lime-400">{cat.billable_hours.toFixed(1)}h</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{cat.non_billable_hours.toFixed(1)}h</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">{formatCurrency(cat.billable_amount)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="border-border text-muted-foreground font-mono">
                        {cat.hours_percent.toFixed(1)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                  {expandedCategories.has(cat.category_id) && (Array.isArray(cat.subcategories) ? cat.subcategories : []).map((sub) => (
                    <TableRow
                      key={sub.category_id}
                      className="border-border bg-card/50"
                      data-testid={`subcategory-row-${sub.category_id}`}
                    >
                      <TableCell className="pl-12">
                        <span className="text-muted-foreground">→ {sub.category_name}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{sub.hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right font-mono text-lime-600 dark:text-lime-400/70">{sub.billable_hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{sub.non_billable_hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400/70">{formatCurrency(sub.billable_amount)}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs text-muted-foreground">{sub.hours_percent.toFixed(1)}%</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
              
              {/* Uncategorized */}
              {data.uncategorized?.hours > 0 && (
                <TableRow className="border-border bg-muted/30" data-testid="uncategorized-row">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-muted-foreground" />
                      <span className="text-muted-foreground">Ohne Kategorie</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{data.uncategorized.hours.toFixed(1)}h</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{data.uncategorized.billable_hours.toFixed(1)}h</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{data.uncategorized.non_billable_hours.toFixed(1)}h</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(data.uncategorized.billable_amount)}</TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs text-muted-foreground">{data.uncategorized.hours_percent.toFixed(1)}%</span>
                  </TableCell>
                </TableRow>
              )}
              
              {/* Totals Row */}
              <TableRow className="border-border bg-muted/50 font-bold">
                <TableCell className="text-foreground">GESAMT</TableCell>
                <TableCell className="text-right font-mono text-foreground">{data.totals.total_hours.toFixed(1)}h</TableCell>
                <TableCell className="text-right font-mono text-lime-600 dark:text-lime-400">{data.totals.billable_hours.toFixed(1)}h</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{data.totals.non_billable_hours.toFixed(1)}h</TableCell>
                <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">{formatCurrency(data.totals.billable_amount)}</TableCell>
                <TableCell className="text-right">
                  <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">100%</Badge>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </motion.div>
    </div>
  );
}
